import React, { useState } from 'react';
import {
    ConfigProvider,
    theme,
    Input,
    Button,
    Table,
    Modal,
    Tag,
    Drawer,
    Checkbox,
} from 'antd';
import {
    Plus,
    FolderKanban,
    Settings,
    Check,
    Trash2,
    Menu,
    X,
} from 'lucide-react';
import Layout from '../../Layout';

export default function ProjectSetup() {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [activeTab, setActiveTab] = useState('project-labels'); 
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

    // ================= STATES CHO DANH MỤC NHÃN & THẺ NHÃN =================
    const [tagCategories, setTagCategories] = useState([
        {
            id: 'job',
            title: 'NHÃN CÔNG VIỆC',
            tags: [
                { id: 1, name: 'Gấp xử lý ngay', color: '#ef4444' },
                { id: 2, name: 'Chuyển đổi số', color: '#22c55e' },
                { id: 3, name: 'Quan trọng', color: '#f59e0b' },
            ]
        },
        {
            id: 'project',
            title: 'NHÃN DỰ ÁN',
            tags: [
                { id: 1, name: 'Quan trọng', color: '#ef4444' },
                { id: 2, name: 'Trúng thầu', color: '#3e91ab' },
                { id: 3, name: '[GTV] Chuẩn', color: '#3b82f6' },
                { id: 4, name: 'Cấp xóa/Thay đổi', color: '#84cc16' },
            ]
        },
        {
            id: 'buglist',
            title: 'NHÃN BUGLIST',
            tags: []
        }
    ]);

    const [isTagModalOpen, setIsTagModalOpen] = useState(false);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [tagName, setTagName] = useState('');
    const [tagColor, setTagColor] = useState('#22c55e');

    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');

    const colorPalette = [
        '#ef4444', '#3e91ab', '#f59e0b', '#84cc16', '#22c55e', 
        '#06b6d4', '#3e91ab', '#3b82f6', '#6366f1', '#a855f7', '#ec4899'
    ];

    const openAddTagModal = (categoryId) => {
        setSelectedCategoryId(categoryId);
        setTagName('');
        setTagColor('#22c55e');
        setIsTagModalOpen(true);
    };

    const handleCreateTag = () => {
        if (!tagName.trim() || !selectedCategoryId) return;
        
        setTagCategories(tagCategories.map(cat => {
            if (cat.id === selectedCategoryId) {
                return {
                    ...cat,
                    tags: [...cat.tags, { id: Date.now(), name: tagName, color: tagColor }]
                };
            }
            return cat;
        }));

        setIsTagModalOpen(false);
    };

    const handleCreateCategory = () => {
        if (!newCategoryName.trim()) return;
        const newCatId = `cat_${Date.now()}`;
        setTagCategories([
            ...tagCategories,
            {
                id: newCatId,
                title: newCategoryName.toUpperCase(),
                tags: []
            }
        ]);
        setNewCategoryName('');
        setIsCategoryModalOpen(false);
    };

    const handleDeleteCategory = (categoryId) => {
        setTagCategories(tagCategories.filter(cat => cat.id !== categoryId));
    };

    // ================= STATES CHO NHÓM DỰ ÁN & DÒNG ĐỘNG TRONG MODAL =================
    const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
    
    // Mảng chứa các dòng nhập trong modal thêm nhóm dự án
    const [groupRows, setGroupRows] = useState([
        { id: 1, name: '', progress: true, material: false, financial: false }
    ]);

    const [projectGroups, setProjectGroups] = useState([
        { key: '1', name: 'Marketing', status: 'Hoạt động', manageProgress: 'Có', manageSub: 'Có', date: '14/12/2022' },
        { key: '2', name: 'Công nghệ thông tin', status: 'Hoạt động', manageProgress: 'Có', manageSub: 'Có', date: '20/04/2023' },
    ]);

    // Thêm dòng mới khi bấm nút dấu cộng trong modal
    const handleAddGroupRow = () => {
        setGroupRows([
            ...groupRows,
            { id: Date.now(), name: '', progress: false, material: false, financial: false }
        ]);
    };

    // Xóa dòng trong modal
    const handleRemoveGroupRow = (id) => {
        if (groupRows.length === 1) return; // Giữ lại ít nhất 1 dòng
        setGroupRows(groupRows.filter(row => row.id !== id));
    };

    // Cập nhật giá trị từng dòng
    const handleGroupRowChange = (id, field, value) => {
        setGroupRows(groupRows.map(row => {
            if (row.id === id) {
                return { ...row, [field]: value };
            }
            return row;
        }));
    };

    // Lưu các nhóm dự án mới vào bảng
    const handleSaveNewGroups = () => {
        const validRows = groupRows.filter(r => r.name.trim() !== '');
        if (validRows.length === 0) {
            setIsGroupModalOpen(false);
            return;
        }

        const newItems = validRows.map((r, index) => ({
            key: String(Date.now() + index),
            name: r.name,
            status: 'Hoạt động',
            manageProgress: r.progress ? 'Có' : 'Không',
            manageSub: r.material ? 'Có' : 'Không',
            date: new Date().toLocaleDateString('vi-VN')
        }));

        setProjectGroups([...newItems, ...projectGroups]);
        setIsGroupModalOpen(false);
        setGroupRows([{ id: 1, name: '', progress: true, material: false, financial: false }]);
    };

    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab',
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            borderRadius: 6,
            controlHeight: 38,
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    const groupColumns = [
        { title: 'Tên nhóm', dataIndex: 'name', key: 'name', render: (text) => <span className="font-medium text-gray-800 dark:text-gray-200">{text}</span> },
        { title: 'Trạng thái', dataIndex: 'status', key: 'status', render: (status) => <span className="px-2.5 py-1 text-xs rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 font-medium">{status}</span> },
        { title: 'Quản lý tiến độ', dataIndex: 'manageProgress', key: 'manageProgress' },
        { title: 'Vật tư', dataIndex: 'manageSub', key: 'manageSub' },
        { title: 'Ngày tạo', dataIndex: 'date', key: 'date' },
    ];

    const SidebarContent = () => (
        <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200 font-semibold mb-6 px-2">
                <Settings size={18} className="text-[#3e91ab]" />
                <span>Cài đặt chung</span>
            </div>
            <div 
                onClick={() => { setActiveTab('project-labels'); setIsMobileMenuOpen(false); }}
                className={`px-3 py-2.5 rounded-lg text-sm cursor-pointer transition ${activeTab === 'project-labels' ? 'bg-[#3e91ab]/10 text-[#3e91ab] font-medium' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
                Nhãn dự án & Công việc
            </div>
            <div 
                onClick={() => { setActiveTab('project-groups'); setIsMobileMenuOpen(false); }}
                className={`px-3 py-2.5 rounded-lg text-sm cursor-pointer transition ${activeTab === 'project-groups' ? 'bg-[#3e91ab]/10 text-[#3e91ab] font-medium' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
                Nhóm dự án
            </div>
        </div>
    );

    return (
        <Layout>
            <ConfigProvider theme={antdThemeConfig}>
                <div className="flex flex-col md:flex-row gap-4 sm:gap-6">
                    
                    {/* --- MOBILE HEADER TOGGLE --- */}
                    <div className="md:hidden bg-white dark:bg-gray-800 border-b dark:border-gray-700 p-4 flex items-center justify-between">
                        <div className="flex items-center gap-2 font-semibold text-gray-800 dark:text-gray-200">
                            <Settings size={18} className="text-[#3e91ab]" />
                            <span>Cài đặt hệ thống</span>
                        </div>
                        <Button 
                            icon={<Menu size={18} />} 
                            onClick={() => setIsMobileMenuOpen(true)}
                        />
                    </div>

                    <Drawer
                        placement="left"
                        onClose={() => setIsMobileMenuOpen(false)}
                        open={isMobileMenuOpen}
                        styles={{ body: { padding: '16px' } }}
                        width={260}
                    >
                        <SidebarContent />
                    </Drawer>

                    <div className="w-64 bg-white dark:bg-gray-800 border-r dark:border-gray-700 p-4 shrink-0 hidden md:block">
                        <SidebarContent />
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        
                        {/* TAB QUẢN LÝ NHÃN */}
                        {activeTab === 'project-labels' && (
                            <div className="flex flex-col gap-6 mx-auto">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                                    <h2 className="text-base font-bold text-gray-800 dark:text-gray-100">Cấu hình danh mục nhãn</h2>
                                    <Button 
                                        type="dashed" 
                                        icon={<Plus size={16} />}
                                        onClick={() => setIsCategoryModalOpen(true)}
                                        style={{ borderColor: '#3e91ab', color: '#3e91ab' }}
                                    >
                                        Thêm danh mục nhãn mới
                                    </Button>
                                </div>

                                {tagCategories.map((category) => (
                                    <div key={category.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-4 sm:p-5 relative group">
                                        <div className="flex justify-between items-center mb-4">
                                            <div className="flex items-center gap-3">
                                                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">{category.title}</h3>
                                                {!['job', 'project', 'buglist'].includes(category.id) && (
                                                    <button 
                                                        onClick={() => handleDeleteCategory(category.id)}
                                                        className="text-red-400 hover:text-red-600 transition"
                                                        title="Xóa danh mục này"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                            <button 
                                                onClick={() => openAddTagModal(category.id)}
                                                className="w-7 h-7 rounded-full border border-[#3e91ab] text-[#3e91ab] flex items-center justify-center hover:bg-orange-50 transition shrink-0"
                                                title="Thêm nhãn mới"
                                            >
                                                <Plus size={16} />
                                            </button>
                                        </div>

                                        {category.tags.length === 0 ? (
                                            <div className="text-xs text-gray-400 italic">Chưa có nhãn nào</div>
                                        ) : (
                                            <div className="flex flex-wrap gap-2">
                                                {category.tags.map((tag) => (
                                                    <Tag key={tag.id} className="px-3 py-1 rounded-full text-xs font-medium border-0 shadow-sm text-white" style={{ backgroundColor: tag.color }}>
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-white/90 inline-block"></span>
                                                            <span>{tag.name}</span>
                                                        </div>
                                                    </Tag>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}

                                <div className="mt-2">
                                    <Button type="primary" style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab', fontWeight: 600 }}>
                                        CẬP NHẬT
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* TAB NHÓM DỰ ÁN */}
                        {activeTab === 'project-groups' && (
                            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border dark:border-gray-700 p-4 sm:p-6 mx-auto">
                                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
                                    <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                                        <FolderKanban size={20} className="text-[#3e91ab]" />
                                        Nhóm dự án
                                    </h2>
                                    <Button type="primary" icon={<Plus size={16} />} onClick={() => setIsGroupModalOpen(true)} style={{ backgroundColor: '#3e91ab' }}>
                                        Thêm mới
                                    </Button>
                                </div>
                                <div className="overflow-x-auto">
                                    <Table dataSource={projectGroups} columns={groupColumns} scroll={{ x: 'max-content' }} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ================= MODAL TẠO MỚI THẺ NHÃN CON ================= */}
                <Modal
                    title={<span className="text-gray-800 dark:text-gray-100 font-semibold">Tạo mới nhãn</span>}
                    open={isTagModalOpen}
                    onCancel={() => setIsTagModalOpen(false)}
                    footer={null}
                    centered
                    width={450}
                >
                    <div className="flex flex-col gap-4 pt-2">
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Tên nhãn</label>
                            <Input placeholder="Nhập tên nhãn" value={tagName} onChange={(e) => setTagName(e.target.value)} />
                        </div>

                        <div>
                            <label className="text-xs text-gray-500 mb-2 block">Chọn màu sắc</label>
                            <div className="flex flex-wrap gap-3 items-center">
                                {colorPalette.map((color, idx) => (
                                    <div 
                                        key={idx}
                                        onClick={() => setTagColor(color)}
                                        className="w-7 h-7 rounded-full cursor-pointer flex items-center justify-center transition hover:scale-110 shadow-sm"
                                        style={{ backgroundColor: color }}
                                    >
                                        {tagColor === color && <Check size={14} className="text-white" />}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700 mt-2">
                            <Button onClick={() => setIsTagModalOpen(false)}>HỦY BỎ</Button>
                            <Button type="primary" onClick={handleCreateTag} style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab' }}>
                                CẬP NHẬT
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* ================= MODAL TẠO MỚI DANH MỤC NHÃN ================= */}
                <Modal
                    title={<span className="text-gray-800 dark:text-gray-100 font-semibold">Thêm danh mục nhãn mới</span>}
                    open={isCategoryModalOpen}
                    onCancel={() => setIsCategoryModalOpen(false)}
                    footer={null}
                    centered
                    width={400}
                >
                    <div className="flex flex-col gap-4 pt-2">
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Tên danh mục nhóm</label>
                            <Input placeholder="Ví dụ: Nhãn phòng ban, Nhãn ưu tiên..." value={newCategoryName} onChange={(e) => setNewCategoryName(e.target.value)} />
                        </div>

                        <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700 mt-2">
                            <Button onClick={() => setIsCategoryModalOpen(false)}>HỦY BỎ</Button>
                            <Button type="primary" onClick={handleCreateCategory} style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab' }}>
                                TẠO MỚI
                            </Button>
                        </div>
                    </div>
                </Modal>

                {/* ================= MODAL TẠO MỚI NHÓM DỰ ÁN (CÓ THÊM HÀNG ĐỘNG +) ================= */}
                <Modal
                    title={<span className="text-gray-800 dark:text-gray-100 font-semibold">Tạo mới nhóm dự án</span>}
                    open={isGroupModalOpen}
                    onCancel={() => setIsGroupModalOpen(false)}
                    footer={null}
                    centered
                    width={650}
                >
                    <div className="flex flex-col gap-4 pt-2 sm:max-h-[60vh] overflow-y-auto px-1">
                        {/* Tiêu đề cột trong Modal */}
                        <div className="grid grid-cols-12 gap-3 text-xs font-semibold text-gray-500 border-b pb-2">
                            <div className="col-span-6">Tên nhóm</div>
                            <div className="col-span-2 text-center">Tiến độ</div>
                            <div className="col-span-2 text-center">Vật tư</div>
                            <div className="col-span-2 text-center">Tài chính</div>
                        </div>

                        {/* Danh sách các dòng nhập */}
                        {groupRows.map((row, index) => (
                            <div key={row.id} className="grid grid-cols-12 gap-3 items-center">
                                <div className="col-span-6">
                                    <Input 
                                        placeholder="Nhập tên nhóm" 
                                        value={row.name} 
                                        onChange={(e) => handleGroupRowChange(row.id, 'name', e.target.value)} 
                                    />
                                </div>
                                <div className="col-span-2 flex justify-center">
                                    <Checkbox 
                                        checked={row.progress} 
                                        onChange={(e) => handleGroupRowChange(row.id, 'progress', e.target.checked)} 
                                    />
                                </div>
                                <div className="col-span-2 flex justify-center">
                                    <Checkbox 
                                        checked={row.material} 
                                        onChange={(e) => handleGroupRowChange(row.id, 'material', e.target.checked)} 
                                    />
                                </div>
                                <div className="col-span-2 flex items-center justify-center gap-2">
                                    <Checkbox 
                                        checked={row.financial} 
                                        onChange={(e) => handleGroupRowChange(row.id, 'financial', e.target.checked)} 
                                    />
                                    {groupRows.length > 1 && (
                                        <button 
                                            onClick={() => handleRemoveGroupRow(row.id)}
                                            className="text-gray-400 hover:text-red-500 transition"
                                        >
                                            <X size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}

                        {/* Nút dấu cộng thêm hàng mới giống hình mẫu */}
                        <div>
                            <button 
                                onClick={handleAddGroupRow}
                                className="w-8 h-8 rounded-full border border-[#3e91ab] text-[#3e91ab] flex items-center justify-center hover:bg-orange-50 transition mt-2"
                                title="Thêm dòng mới"
                            >
                                <Plus size={16} />
                            </button>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t dark:border-gray-700 mt-4">
                        <Button onClick={() => setIsGroupModalOpen(false)}>HỦY BỎ</Button>
                        <Button type="primary" onClick={handleSaveNewGroups} style={{ backgroundColor: '#3e91ab', borderColor: '#3e91ab' }}>
                            CẬP NHẬT
                        </Button>
                    </div>
                </Modal>
            </ConfigProvider>
        </Layout>
    );
}